// eslint-disable-next-line @typescript-eslint/ban-ts-comment
import { interpolateRainbow } from 'd3-scale-chromatic';
import isArray from 'lodash-es/isArray';
import isEmpty from 'lodash-es/isEmpty';
import isNil from 'lodash-es/isNil';
import { scaleLinear } from 'victory-vendor/d3-scale';
import { Resource } from '../Resource';
import { apiVersion } from '../apiVersion';
import { declineColorForHorizon } from '../core/colors';
import { isMetricCompatibleWithExport, isMetricWithHorizon } from '../enums/MetricIds';
import * as transformers from '../transformers';
import * as methods from './../methods';
import * as devices from './devices';
import { fromRaw as fromRawMeasurementConfig } from './measurementConfigs';
import { plotMutationExternalInvalidationKeys } from './plots';
import { stationsMutationExternalInvalidationKeys } from './stations';
const rootPath = 'measurements';
export const rawHistoryParser1_1 = (raw) => {
    const initialHistory = {
        since: raw.metadata.since,
        before: raw.metadata.before,
        span: raw.metadata.span,
        values: {},
        horizons: {},
        colorBySerial: {}
    };
    const history = Object.entries(raw.metadata.series_config).reduce((acc, [key, series]) => {
        const metric = series.measurement_type;
        if (isMetricWithHorizon(metric) && series.horizon && acc.horizons) {
            const { horizon } = series;
            // init metric horizon details
            if (!acc.horizons[metric]) {
                acc.horizons[metric] = {};
            }
            ;
            acc.horizons[metric][horizon.id] = {
                ...horizon,
                color: declineColorForHorizon(metric, horizon)
            };
            let colorBySerial = acc.colorBySerial?.[metric];
            if (acc.colorBySerial && !acc.colorBySerial[metric]) {
                colorBySerial = {};
                acc.colorBySerial[metric] = colorBySerial;
            }
            if (colorBySerial && series.serial !== null) {
                colorBySerial[series.serial] = { color: '', depth: horizon.value };
            }
            //init metric horizon values
            // we create tmp variable that we associate back to accumulator this way typescript can infer that object is define correctly
            // this code seems a bit odd but this way we avoid casting which could lead to runtime difference
            if (!acc.values[metric]?.[horizon.id]) {
                let measurementValues = acc.values[metric];
                if (!measurementValues) {
                    measurementValues = {};
                    // @ts-expect-error since ts@4.9
                    acc.values[metric] = measurementValues;
                }
                measurementValues[horizon.id] = [];
            }
            raw.data[key].forEach((historyEntry) => {
                const horizonValues = acc.values[metric]?.[horizon.id];
                if (horizonValues) {
                    horizonValues.push({
                        ...historyEntry,
                        serial: series.serial
                    });
                }
            });
        }
        else {
            const tempValues = acc.values;
            // @ts-expect-error since ts@5.X
            tempValues[metric] = acc.values[metric] || {};
            const tempMeasurementTypes = tempValues[metric] || {};
            tempMeasurementTypes[series.aggregate] = raw.data[key];
            // @ts-expect-error since ts@4.9
            tempValues[metric] = tempMeasurementTypes;
            acc.values = tempValues;
        }
        return acc;
    }, initialHistory);
    Object.entries(history.colorBySerial || {}).forEach(([metric, colorPerSerial]) => {
        const serials = Object.keys(colorPerSerial);
        const scaleSerial = scaleLinear().domain([0, serials.length]).range([0, 1]);
        serials.forEach((serial, idx) => {
            const colorBySerial = history.colorBySerial?.[metric];
            if (colorBySerial && colorBySerial[serial] !== undefined) {
                colorBySerial[serial] = {
                    ...colorBySerial[serial],
                    color: interpolateRainbow(scaleSerial(idx))
                };
            }
        });
    });
    return history;
};
export const fromRawPlotSummary = ({ metadata: { plot, sunrise, sunset, not_configured }, data }) => ({
    metadata: {
        plot: {
            currentMeasurementConfigs: plot.current_measurement_configs
                ? plot.current_measurement_configs.map(fromRawMeasurementConfig)
                : [],
            isFavorite: plot.favorite,
            id: plot.id,
            name: plot.name,
            location: plot.location,
            reverseGeocoding: plot.location_text,
            hasVirtualDevice: plot.virtual_device
        },
        sunrise,
        sunset,
        notConfigured: not_configured
    },
    data
});
export const fromRawDeviceSummary = ({ metadata: { device, sunrise, sunset }, data }) => ({
    metadata: {
        device: {
            availableMeasures: device.available_measures,
            brand: device.brand,
            endUserName: device.enduser_name,
            id: device.id,
            location: device.location,
            model: device.model,
            reverseGeocoding: device.location_text,
            serial: device.serial
        },
        sunrise,
        sunset
    },
    data
});
export const fromRawStationSummary = ({ metadata: { station, sunrise, sunset }, data }) => ({
    metadata: {
        station: {
            availableMeasures: station.available_measures,
            brand: station.brand,
            deviceId: station.org_device,
            endUserName: station.enduser_name,
            id: station.id,
            isFavorite: station.favorite,
            location: station.location,
            reverseGeocoding: station.location_text,
            model: station.model,
            name: station.name ?? station.location_text,
            isDynamicName: isNil(station.name),
            serial: station.serial
        },
        sunrise,
        sunset
    },
    data
});
const fromRaw = {
    universes: ({ universes }) => universes,
    plots: {
        summaries: {
            getMany: fromRawPlotSummary
        }
    },
    devices: {
        summaries: {
            getMany: fromRawDeviceSummary
        }
    },
    stations: {
        summaries: {
            getMany: fromRawStationSummary
        }
    },
    history: {
        get1_1: rawHistoryParser1_1
    },
    measurements: {
        export: (raw) => raw
    },
    plotsAndStations: {
        summaries: {
            getMany: ({ metadata, data }) => {
                if (!('station' in metadata || 'plot' in metadata)) {
                    throw new Error('wrong summary passed to plotsAndStations.summaries.getMany expected at least station or plot in metadata');
                }
                if ('plot' in metadata) {
                    return fromRawPlotSummary({ metadata, data });
                }
                else {
                    return fromRawStationSummary({ metadata, data });
                }
            }
        }
    }
};
const cacheConfig = {
    staleTime: 1 * 60 * 1000
};
const buildHistoryQueryParam = ({ kind, query: { before, metrics, since, aggregateHorizon = true, aggregateByMetric = false, limit, offset, plotId, span, deviceId, stationId } }) => {
    const query = {
        measurements: metrics,
        limit: limit,
        offset: offset,
        since: since,
        before: before,
        span: span
    };
    return kind === 'plot'
        ? {
            ...query,
            plot: plotId,
            aggregate_horizon: aggregateHorizon,
            aggregate_by_metric: aggregateByMetric
        }
        : kind === 'device'
            ? {
                ...query,
                device: deviceId
            }
            : {
                ...query,
                station: stationId
            };
};
const measurements = Resource((requesterBuilder) => {
    const getAvailableUniverses = (query) => {
        const requester = methods.get({
            requesterBuilder,
            rootPath: `${rootPath}/plots/summary/universes`,
            params: {
                query: {
                    organization: query?.organizationId
                }
            },
            fromRaw: fromRaw.universes
        })();
        plotMutationExternalInvalidationKeys.add(requester.invalidationKey[0]);
        return requester;
    };
    const createGetManySummaries = (resource, transformer, endRootPath = '') => (query) => {
        const newRequester = requesterBuilder({
            path: `${rootPath}/${resource}/summary${endRootPath}`,
            requestCtx: {
                transform: transformers.paginatedResponse(transformer),
                params: {
                    query: {
                        universe: query?.universe,
                        organization: query?.organizationId,
                        limit: query?.limit,
                        offset: query?.offset
                        // to enabled once every summary on device map has been added
                        // full: true
                    }
                },
                ...apiVersion[1.1]
            },
            config: cacheConfig
        });
        plotMutationExternalInvalidationKeys.add(getAvailableUniverses({ organizationId: query?.organizationId })
            .invalidationKey[0]);
        if (resource === 'plots') {
            plotMutationExternalInvalidationKeys.add(newRequester.key[0]);
        }
        else if (resource === 'devices') {
            devices.deviceMutationExternalInvalidationKeys.add(newRequester.key[0]);
        }
        else if (resource === 'plots_and_stations') {
            // adding a device also create a station so we need to add it to the list to invalidate on device edition
            devices.deviceMutationExternalInvalidationKeys.add(newRequester.key[0]);
            plotMutationExternalInvalidationKeys.add(newRequester.key[0]);
            stationsMutationExternalInvalidationKeys.add(newRequester.key[0]);
        }
        return newRequester;
    };
    const getManyPlotSummaries = createGetManySummaries('plots', fromRaw.plots.summaries.getMany);
    const getPlotSummary = (query) => requesterBuilder({
        path: `${rootPath}/plots/summary/${query.plotId}`,
        requestCtx: {
            transform: fromRaw.plots.summaries.getMany,
            params: {
                query: {
                    limit: query.limit,
                    offset: query.offset
                }
            },
            ...apiVersion[1.1]
        }
    });
    const getManyDeviceSummaries = createGetManySummaries('devices', fromRaw.devices.summaries.getMany);
    const getManyPlotOrStationSummaries = createGetManySummaries('plots_and_stations', fromRaw.plotsAndStations.summaries.getMany);
    const getDeviceSummary = (query) => requesterBuilder({
        path: `${rootPath}/devices/summary/${query.deviceId}`,
        requestCtx: {
            transform: fromRaw.devices.summaries.getMany,
            params: {
                query: {
                    limit: query.limit,
                    offset: query.offset
                }
            },
            ...apiVersion[1.1]
        }
    });
    const getStationSummary = (query) => requesterBuilder({
        path: `${rootPath}/stations/summary/${query.stationId}`,
        requestCtx: {
            transform: fromRaw.stations.summaries.getMany,
            params: {
                query: {
                    limit: query.limit,
                    offset: query.offset
                }
            },
            ...apiVersion[1.1]
        }
    });
    const makeDownload = (path) => ({ metrics, format = 'xlsx', since, before, span, id, aggregateByMetric = false, aggregateHorizons = false }) => requesterBuilder({
        path: `${rootPath}/${path}/history/export`,
        requestCtx: {
            method: 'GET',
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            parse: (x) => x.blob(),
            transform: (blob) => blob,
            params: {
                query: {
                    since,
                    before,
                    span,
                    measurements: metrics.filter((metric) => isMetricCompatibleWithExport(metric)),
                    aggregate_horizon: aggregateHorizons,
                    aggregate_by_metric: aggregateByMetric,
                    export_format: format,
                    ...{ [path === 'plots' ? 'plot' : 'device']: id }
                }
            },
            ...apiVersion[1.1]
        }
    });
    const exportPlotsData = ({ aggregateByMetric = false, aggregateHorizons = false, before, format = 'xlsx', metrics, plots, since, timeStep, orgId }) => requesterBuilder({
        path: `${rootPath}/plots/history/export`,
        requestCtx: {
            method: 'GET',
            params: {
                query: {
                    organization: orgId,
                    aggregate_by_metric: aggregateByMetric,
                    aggregate_horizon: aggregateHorizons,
                    before,
                    export_format: format,
                    measurements: isArray(metrics) ? metrics.join(',') : metrics,
                    plot: isArray(plots) ? plots.join(',') : plots,
                    since,
                    span: timeStep
                }
            },
            transform: (raw) => {
                if (!isNil(raw)) {
                    return fromRaw.measurements.export(raw);
                }
                else {
                    return undefined;
                }
            },
            parse: async (response) => {
                const text = await response.text();
                if (!isEmpty(text)) {
                    return JSON.parse(text);
                }
                else {
                    return undefined;
                }
            },
            ...apiVersion[1.1]
        }
    });
    return {
        plots: {
            summaries: {
                getMany: getManyPlotSummaries,
                getAvailableUniverses,
                // TODO: rename to get
                getOne: getPlotSummary,
                getAllPages: methods.getAllPages({
                    getMany: getManyPlotSummaries,
                    cacheConfig,
                    limit: 1000
                })
            },
            histories: {
                download: makeDownload('plots'),
                export: exportPlotsData,
                get1_1: (query) => {
                    const newRequester = requesterBuilder({
                        path: `${rootPath}/plots/history`,
                        requestCtx: {
                            transform: (raw) => {
                                if (typeof raw !== 'undefined' && raw !== null && raw.length > 0) {
                                    // this endpoint return data as an array of one result always so we force raw[0]
                                    return fromRaw.history.get1_1(raw[0]);
                                }
                                else {
                                    return undefined;
                                }
                            },
                            params: {
                                query: buildHistoryQueryParam({ kind: 'plot', query })
                            },
                            ...apiVersion[1.1]
                        }
                    });
                    plotMutationExternalInvalidationKeys.add(newRequester.key[0]);
                    return newRequester;
                }
            }
        },
        devices: {
            summaries: {
                getAvailableUniverses: () => methods.get({
                    requesterBuilder,
                    rootPath: `${rootPath}/devices/summary/universes`,
                    fromRaw: fromRaw.universes
                })(),
                getMany: getManyDeviceSummaries,
                getOne: getDeviceSummary,
                getAllPages: methods.getAllPages({
                    getMany: getManyDeviceSummaries,
                    cacheConfig,
                    limit: 1000
                })
            },
            histories: {
                download: makeDownload('devices'),
                get1_1: (query) => requesterBuilder({
                    path: `${rootPath}/devices/history`,
                    requestCtx: {
                        transform: (raw) => {
                            if (typeof raw !== 'undefined' && raw !== null && raw.length > 0) {
                                return fromRaw.history.get1_1(raw[0]);
                            }
                            else {
                                return undefined;
                            }
                        },
                        params: {
                            query: buildHistoryQueryParam({ kind: 'device', query })
                        },
                        ...apiVersion[1.1]
                    }
                })
            }
        },
        stations: {
            summaries: {
                getOne: getStationSummary
            },
            histories: {
                get1_1: (query) => requesterBuilder({
                    path: `${rootPath}/stations/history`,
                    requestCtx: {
                        transform: (raw) => {
                            if (typeof raw !== 'undefined' && raw !== null && raw.length > 0) {
                                return fromRaw.history.get1_1(raw[0]);
                            }
                            else {
                                return undefined;
                            }
                        },
                        params: {
                            query: buildHistoryQueryParam({ kind: 'station', query })
                        },
                        ...apiVersion[1.1]
                    }
                })
            }
        },
        plotsAndStations: {
            summaries: {
                getAvailableUniverses: (query) => {
                    const requester = methods.get({
                        requesterBuilder,
                        rootPath: `${rootPath}/plots_and_stations/summary/universes`,
                        fromRaw: fromRaw.universes,
                        params: {
                            query: {
                                organization: query.organizationId
                            }
                        }
                    })();
                    const invalidationKey = requester.invalidationKey[0];
                    plotMutationExternalInvalidationKeys.add(invalidationKey);
                    stationsMutationExternalInvalidationKeys.add(invalidationKey);
                    return requester;
                },
                getMany: getManyPlotOrStationSummaries,
                getAllPages: methods.getAllPages({
                    getMany: getManyPlotOrStationSummaries,
                    cacheConfig,
                    limit: 1000
                })
            }
        }
    };
});
export default measurements;
